import { useEffect, useRef } from 'react';
import type { Celebration } from '@numslearn/shared';
import { playApplause, playPraise } from '../sound.js';
import type { StringKey, Translate } from '../i18n/index.js';

/**
 * What happens the moment a child gets one right.
 *
 * Three levels, because a Year 1 room and a Year 6 room want different things
 * from the same correct answer, and the level is a teacher setting:
 *
 *   sparks — the original: coloured sparks off the tile and a rising chime.
 *   word   — plus a word of praise over the board and a ring snapping outward.
 *   full   — plus confetti, a stamped star, and applause on every third match.
 *
 * The praise word rotates through five so it never becomes wallpaper, and the
 * whole thing is skipped under `prefers-reduced-motion` — the sound still
 * plays, because someone who asked for less motion did not ask for less
 * encouragement.
 */

const WORDS: StringKey[] = ['praise.1', 'praise.2', 'praise.3', 'praise.4', 'praise.5'];

interface Props {
  /** Bumped on every correct answer; nothing fires when it does not change. */
  nonce: number;
  level: Celebration;
  combo: number;
  t: Translate;
}

export function Praise({ nonce, level, combo, t }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const seen = useRef(0);
  const word = useRef(0);

  useEffect(() => {
    if (nonce === 0 || nonce === seen.current) return;
    seen.current = nonce;
    if (level === 'sparks') return;

    const el = host.current;
    if (!el) return;

    playPraise();
    if (level === 'full' && combo > 0 && combo % 3 === 0) playApplause(1200);

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const box = el.getBoundingClientRect();
    const cleanup: number[] = [];
    const add = (node: HTMLElement, ms: number) => {
      el.appendChild(node);
      cleanup.push(window.setTimeout(() => node.remove(), ms));
    };

    const span = (cls: string): HTMLSpanElement => {
      const s = document.createElement('span');
      s.className = cls;
      return s;
    };

    const say = span(`praise-word ${level === 'full' ? 'is-big' : 'is-mid'}`);
    say.textContent = t(WORDS[word.current % WORDS.length]!);
    word.current += 1;
    add(say, 1400);

    const ring = span('praise-ring');
    add(ring, 800);

    if (level === 'full') {
      for (let k = 0; k < 40; k++) {
        const c = span(`confetti-bit${k % 3 === 0 ? ' is-round' : k % 3 === 1 ? ' is-ribbon' : ''}`);
        const angle = (k / 40) * Math.PI * 2 + (k % 3) * 0.2;
        const dist = 110 + ((k * 41) % 150);
        c.style.left = `${box.width / 2}px`;
        c.style.top = `${box.height * 0.44}px`;
        c.style.background = `var(--g-c${(k % 4) + 1})`;
        c.style.setProperty('--dx', `${Math.round(Math.cos(angle) * dist)}px`);
        // Gravity: confetti thrown up still comes down.
        c.style.setProperty('--dy', `${Math.round(Math.sin(angle) * dist + 70)}px`);
        c.style.setProperty('--rot', `${((k * 67) % 720) - 360}deg`);
        c.style.setProperty('--dur', `${900 + (k % 7) * 90}ms`);
        c.style.animationDelay = `${(k % 6) * 24}ms`;
        add(c, 1900);
      }
      const star = span('praise-stamp');
      star.textContent = '⭐';
      add(star, 1300);
    }

    return () => { for (const id of cleanup) window.clearTimeout(id); };
  }, [nonce, level, combo, t]);

  return <div className="praise-layer" ref={host} aria-hidden="true" />;
}
